/**
 * `Icon` (W1-12 ui-kit-i18n; contract IconProps): a lucide-react icon looked up by name. Canonical names
 * ('Play', 'Magnet', 'TriangleAlert') and lucide's alias exports ('Trash2', 'History', 'AlertTriangle',
 * 'PlayIcon', 'LucidePlay') both resolve. Decorative icons are aria-hidden; labelled ones get role="img" and an
 * accessible name. Unknown names render a neutral placeholder glyph (and warn once in dev) instead of failing.
 *
 * W3-01 (UX-006): the bundle carries only the icons the app names (iconSet.ts, generated); a valid lucide name that
 * is not in that set loads that one icon on demand (lucide's per-icon chunks) and appears when it arrives.
 * `isIconName` answers from the list of names (lucideNames.ts) without loading any icon.
 */

import type { LucideIcon } from 'lucide-react';
import { createElement, useEffect, useSyncExternalStore } from 'react';
import type { IconName } from '../app/registry/types';
import { useTr } from './bridge';
import { ICON_SET } from './iconSet';
import { ALIASES, CANONICAL, FILE_EXCEPTIONS } from './lucideNames';
import type { IconProps } from './types';

const CANONICAL_SET: ReadonlySet<string> = new Set(CANONICAL);
const warned = new Set<string>();

/** The canonical lucide name of a name or alias ('AlertTriangle', 'PlayIcon', 'LucidePlay'), or null. */
export function canonicalIconName(name: string): string | null {
  for (const candidate of [name, name.replace(/Icon$/, ''), name.replace(/^Lucide/, '')]) {
    if (CANONICAL_SET.has(candidate)) return candidate;
    const alias = ALIASES[candidate];
    if (alias !== undefined) return alias;
  }
  return null;
}

/** True when `name` is a lucide icon (feature modules can validate their icon names in tests). */
export function isIconName(name: string): boolean {
  return canonicalIconName(name) !== null;
}

// ---- icons outside ICON_SET, loaded one by one on demand ----

/** Plain kebab-case of an icon name ('ZoomIn' -> 'zoom-in', 'Music4' -> 'music-4'). */
export function kebabIconName(canonical: string): string {
  return canonical
    .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
    .replace(/([A-Z])([A-Z][a-z])/g, '$1-$2')
    .replace(/([a-zA-Z])(\d)/g, '$1-$2')
    .toLowerCase();
}

/** The module file name of a canonical icon name ('Grid3x3' -> 'grid-3x3', 'ArrowDownAZ' -> 'arrow-down-a-z'). */
export function iconFileName(canonical: string): string {
  return FILE_EXCEPTIONS[canonical] ?? kebabIconName(canonical);
}

const onDemand = new Map<string, LucideIcon>();
const requested = new Set<string>();
let onDemandVersion = 0;
const onDemandListeners = new Set<() => void>();

function loadOnDemand(canonical: string): void {
  if (onDemand.has(canonical) || requested.has(canonical)) return;
  requested.add(canonical);
  import('lucide-react/dynamicIconImports')
    .then(async (mod) => {
      const imports = mod.default as Readonly<
        Record<string, (() => Promise<{ readonly default: unknown }>) | undefined>
      >;
      const load = imports[iconFileName(canonical)];
      if (load === undefined) return;
      const icon = (await load()).default as LucideIcon;
      onDemand.set(canonical, icon);
      onDemandVersion++;
      for (const cb of [...onDemandListeners]) cb();
    })
    .catch(() => {
      requested.delete(canonical);
    });
}

function subscribeOnDemand(cb: () => void): () => void {
  onDemandListeners.add(cb);
  return () => {
    onDemandListeners.delete(cb);
  };
}

const getOnDemandVersion = (): number => onDemandVersion;

/**
 * The lucide component for a name (canonical or alias): from the bundled set, else the icon loaded on demand; null
 * when lucide has no icon of that name or it has not loaded yet.
 */
export function lucideIcon(name: IconName): LucideIcon | null {
  const canonical = canonicalIconName(name);
  if (canonical === null) return null;
  return ICON_SET[canonical] ?? onDemand.get(canonical) ?? null;
}

const FALLBACK: LucideIcon = ICON_SET.CircleDashed as LucideIcon;

export function Icon(props: IconProps) {
  const tr = useTr();
  const size = props.size ?? 16;
  useSyncExternalStore(subscribeOnDemand, getOnDemandVersion, getOnDemandVersion);
  const found = lucideIcon(props.name);
  const name = props.name;
  const known = isIconName(name);
  const waiting = found === null && known;
  useEffect(() => {
    if (found !== null) return;
    if (known) {
      const canonical = canonicalIconName(name);
      if (canonical !== null) loadOnDemand(canonical);
      if (import.meta.env.DEV && !warned.has(name)) {
        warned.add(name);
        console.info(`[ui] icon "${name}" is not in src/ui/iconSet.ts (loaded on demand; refresh the file)`);
      }
    } else if (import.meta.env.DEV && !warned.has(name)) {
      warned.add(name);
      console.warn(`[ui] unknown lucide icon "${name}"`);
    }
  }, [found, known, name]);
  const a11y =
    props.label === undefined
      ? ({ 'aria-hidden': true } as const)
      : ({ role: 'img', 'aria-label': tr(props.label) } as const);
  const data = { 'data-icon': props.name };
  if (waiting) {
    // Same box as the icon while it loads (no placeholder glyph flashing).
    return createElement('span', {
      ...data,
      ...a11y,
      style: {
        display: 'inline-block',
        flex: 'none',
        width: `calc(${String(size)}px * var(--ui-scale, 1))`,
        height: `calc(${String(size)}px * var(--ui-scale, 1))`,
      },
    });
  }
  return createElement(found ?? FALLBACK, {
    size,
    strokeWidth: size <= 14 ? 2 : 1.75,
    focusable: false,
    ...data,
    // Scales with the chrome (Settings > UI scale); the numeric size is the 100 % size in CSS pixels.
    style: { flex: 'none', width: `calc(${String(size)}px * var(--ui-scale, 1))`, height: 'auto' },
    ...a11y,
  });
}
